import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CircularProgress, Stack, Typography } from '@mui/material'

import useAuth, { setWorkosAccessToken } from '../models/useAuth'
import { rememberEmail } from '../lib/rememberedEmail'
import { finalizeCallback } from '../utilities/authCallback'

type CallbackState = 'loading' | 'ready' | 'error'

const clearAuthParams = () => {
  if (typeof window === 'undefined') {
    return
  }

  const { pathname, search } = window.location
  window.history.replaceState({}, '', `${pathname}${search}`)
}

export default function AuthCallbackPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const hasProcessedRef = useRef(false)
  const [status, setStatus] = useState<CallbackState>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [redirectPath, setRedirectPath] = useState('/')

  useEffect(() => {
    let cancelled = false

    if (hasProcessedRef.current) {
      return
    }

    const finalizeAuth = async () => {
      try {
        // finalizeCallback clears the fragment before any step that can throw, so
        // the error page never shows the access token (app#377).
        const nextPath = finalizeCallback(location.search, location.hash, {
          clearAuthParams,
          // Keep the WorkOS token in memory for the Supabase client (RLS). The
          // router also sets HttpOnly cookies.
          setAccessToken: token => setWorkosAccessToken(token),
          // This is the only place both halves are in hand: the email (a token claim)
          // and how it was authenticated (the router's `auth_method`). Outlives the
          // session on purpose — it exists to prefill the NEXT sign-in.
          remember: authMethod => rememberEmail(useAuth.getState().user?.email ?? null, authMethod),
        })
        setRedirectPath(nextPath)

        if (!cancelled) {
          hasProcessedRef.current = true
          setStatus('ready')
        }
      } catch (error) {
        console.error('AuthCallbackPage: failed to finalize session', error)

        if (!cancelled) {
          hasProcessedRef.current = false
          setErrorMessage(error instanceof Error ? error.message : 'Unexpected error')
          setStatus('error')
        }
      }
    }

    void finalizeAuth()

    return () => {
      cancelled = true
    }
  }, [location.hash, location.search])

  useEffect(() => {
    if (status !== 'ready') {
      return
    }

    navigate(redirectPath, { replace: true })
  }, [navigate, redirectPath, status])

  const isLoading = useMemo(() => status === 'loading', [status])

  if (status === 'error') {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 4
        }}>
        <Stack spacing={2} sx={{ maxWidth: 420, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Authentication failed
          </Typography>
          <Typography sx={{
            color: 'text.secondary'
          }}>{errorMessage ?? 'Unable to complete sign-in.'}</Typography>
        </Stack>
      </Stack>
    )
  }

  if (isLoading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}>
        <Stack spacing={2} sx={{
          alignItems: 'center'
        }}>
          <CircularProgress />
          <Typography variant="body1">Signing you in…</Typography>
          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            Please wait while we complete your authentication.
          </Typography>
        </Stack>
      </Stack>
    )
  }

  return null
}
